import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { debounceTime, filter, finalize, Subject, take, takeUntil } from 'rxjs';
import { DashboardSectionHeaderComponent } from 'src/app/notification/dashboard/dashboard-section-header/dashboard-section-header.component';
import { NotificationViewModel } from 'src/app/notification/notification.view-model';
import { MaterialModule } from 'src/app/shared/material/material.module';
import { FormUtils } from 'src/app/shared/utils/form-utils';
import { SkeletonComponent } from 'src/app/shared/skeleton/skeleton.component';
import { AuthCognitoService } from '../../auth/auth-cognito.service';
import { subjectLabels, subjectWord } from '../../configuration/subject-label.util';
import { activeAlertsHeader } from '../callback-location.util';
import { groupActiveAlertsByCampaign } from '../active-campaign-card/active-campaign-card.util';
import { ActiveCampaignCardComponent } from '../active-campaign-card/active-campaign-card.component';
import { ContactsViewModel } from '../contacts.view-model';
import { resolveAlertConfig } from '../../configuration/configuration.defaults';
import { OrgActiveAlert } from '../patient.model';
import { OrganizationsComponent } from '../organizations/organizations.component';
import { PatientService } from '../patient.service';
import { Organization, UpdateProfileRequest } from '../user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formatNextAlertAttemptLabel } from '../../notification/utils/alert-progress.util';
import { CYCLE_STATUS_LEGEND } from '../../notification/cycle-status.util';

const ACTIVE_ALERTS_PAGE_SIZE = 50;

type ActiveAlertSortKey =
  | 'initiatedOn'
  | 'campaignTitle'
  | 'nextAlertAt'
  | 'scheduledEndAt'
  | 'alertSubject'
  | 'contactPhone';

@Component({
  selector: 'app-contact-list',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    MatSidenavModule,
    ReactiveFormsModule,
    RouterModule,
    DashboardSectionHeaderComponent,
    OrganizationsComponent,
    SkeletonComponent,
    ActiveCampaignCardComponent,
  ],
  templateUrl: './contact-list.component.html',
  styleUrl: './contact-list.component.scss',
  host: {
    class: 'contact-list',
  },
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ContactListComponent implements OnInit, OnDestroy {
  #snackBar = inject(MatSnackBar);
  #router = inject(Router);
  #authCognitoService = inject(AuthCognitoService);
  #patientService = inject(PatientService);
  #contactsViewModel = inject(ContactsViewModel);
  #notificationViewModel = inject(NotificationViewModel);

  readonly pageSize = ACTIVE_ALERTS_PAGE_SIZE;

  readonly activeCampaignStatusLegend = CYCLE_STATUS_LEGEND;

  readonly activeAlertColumns: ReadonlyArray<{ key: ActiveAlertSortKey; labelKey: 'fixed' | 'subject' }> =
    [
      { key: 'initiatedOn', labelKey: 'fixed' },
      { key: 'campaignTitle', labelKey: 'fixed' },
      { key: 'nextAlertAt', labelKey: 'fixed' },
      { key: 'scheduledEndAt', labelKey: 'fixed' },
      { key: 'alertSubject', labelKey: 'subject' },
      { key: 'contactPhone', labelKey: 'fixed' },
    ];

  activeAlerts = signal<OrgActiveAlert[]>([]);
  activeAlertsLoading = signal(true);
  /** Bumped after active alert list reload so campaign cards refresh status/history. */
  activeCampaignsRevision = signal(0);
  /** Alert row ids with a cancel request in flight (immediate STOP → STOPPING… feedback). */
  stoppingAlertIds = signal<ReadonlySet<string>>(new Set());
  searchTerm = signal('');
  pageIndex = signal(0);
  activeAlertSortKey = signal<ActiveAlertSortKey>('initiatedOn');
  activeAlertSortDirection = signal<'asc' | 'desc'>('desc');

  searchForm: FormGroup = new FormGroup({
    search: new FormControl<string>('', []),
  });

  profile$ = this.#contactsViewModel.profile$;
  profileError$ = this.#contactsViewModel.profileError$;
  profile = toSignal(this.profile$);
  currentOrganization = toSignal(this.#contactsViewModel.currentOrganization$);

  @ViewChild('drawer', { static: true })
  organizationMenu!: MatSidenav;

  destroy$ = new Subject<void>();

  activeCampaignGroups = computed(() => groupActiveAlertsByCampaign(this.activeAlerts()));

  orgAlertConfig = computed(() => resolveAlertConfig(this.currentOrganization()?.alertConfig));

  filteredActiveAlerts = computed(() => filterActiveAlerts(this.activeAlerts(), this.searchTerm()));

  sortedFilteredActiveAlerts = computed(() =>
    sortActiveAlerts(
      this.filteredActiveAlerts(),
      this.activeAlertSortKey(),
      this.activeAlertSortDirection(),
    ),
  );

  pagedActiveAlerts = computed(() => {
    const all = this.sortedFilteredActiveAlerts();
    const start = this.pageIndex() * ACTIVE_ALERTS_PAGE_SIZE;
    return all.slice(start, start + ACTIVE_ALERTS_PAGE_SIZE);
  });

  subjectColumnLabel = computed(() => subjectLabels(this.currentOrganization()).singular);

  activeAlertsSearchPlaceholder = computed(() => {
    const word = subjectWord(this.subjectColumnLabel());
    return `Search ${word}, number, or campaign...`;
  });

  activeAlertsHeaderText = computed(() =>
    activeAlertsHeader(this.filteredActiveAlerts().length, this.activeAlertsLoading()),
  );

  hasPreviousPage = computed(() => this.pageIndex() > 0);

  hasNextPage = computed(
    () => (this.pageIndex() + 1) * ACTIVE_ALERTS_PAGE_SIZE < this.filteredActiveAlerts().length,
  );

  constructor() {
    this.searchForm.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((value) => {
      this.searchTerm.set((value.search ?? '').trim().toLowerCase());
      this.pageIndex.set(0);
    });
  }

  onOrganizationSelected(org: Organization) {
    this.organizationMenu.toggle();
    if (org.id !== this.profile()?.currentOrg) {
      const args: UpdateProfileRequest = {
        name: this.profile()?.name || '',
        currentOrg: org.id,
      };
      this.#contactsViewModel.updateOrganization(args, org);
      this.pageIndex.set(0);
      this.loadActiveAlerts();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter((event) => event.urlAfterRedirects.startsWith('/contacts')),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.loadActiveAlerts());

    this.profile$
      .pipe(
        filter((p) => !!p),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loadActiveAlerts();
      });
    this.profile$
      .pipe(
        take(1),
        filter((profile) => !profile),
      )
      .subscribe(() => {
        this.#contactsViewModel.loadProfile();
      });
    this.profileError$
      .pipe(
        takeUntil(this.destroy$),
        filter((e) => !!e),
      )
      .subscribe(() => {
        const ref = this.#snackBar.open("Sorry, we couldn't load the user information.", 'Okay', {
          duration: 5000,
        });
        ref.afterDismissed().subscribe(() => {
          this.onLogout();
        });
      });
  }

  loadActiveAlerts(): void {
    this.activeAlertsLoading.set(true);
    this.#patientService
      .getActiveAlertPatientIds()
      .pipe(take(1))
      .subscribe({
        next: ({ result }) => {
          this.activeAlerts.set(result ?? []);
          this.activeAlertsLoading.set(false);
          this.activeCampaignsRevision.update((v) => v + 1);
        },
        error: (err) => {
          console.error('Error loading active alerts', err);
          this.activeAlerts.set([]);
          this.activeAlertsLoading.set(false);
          this.activeCampaignsRevision.update((v) => v + 1);
        },
      });
  }

  onPreviousPage(): void {
    if (this.hasPreviousPage()) {
      this.pageIndex.update((i) => i - 1);
    }
  }

  onNextPage(): void {
    if (this.hasNextPage()) {
      this.pageIndex.update((i) => i + 1);
    }
  }

  onLogout(): void {
    this.#authCognitoService.signOut().then(() => {
      sessionStorage.removeItem('patientId');
      sessionStorage.removeItem('organizationId');
    });
  }

  formatPhone(phone: string | undefined): string {
    if (!phone) {
      return '';
    }
    return FormUtils.formatPhoneNumber(phone);
  }

  formatDate(epochMs: number | undefined): string {
    if (!epochMs) {
      return '—';
    }
    return new Date(epochMs).toLocaleString();
  }

  /** Split locale datetime so narrow columns wrap date / time, not mid-token. */
  splitDateTime(epochMs: number | undefined): { date: string; time: string } | null {
    if (!epochMs) {
      return null;
    }
    const full = new Date(epochMs).toLocaleString();
    const commaIndex = full.indexOf(', ');
    if (commaIndex >= 0) {
      return {
        date: full.slice(0, commaIndex),
        time: full.slice(commaIndex + 2),
      };
    }
    return { date: full, time: '' };
  }

  nextAlertAttemptLabel(alert: OrgActiveAlert): string | null {
    if (alert.nextAlertAt == null) {
      return null;
    }
    return formatNextAlertAttemptLabel(alert);
  }

  campaignLabel(alert: OrgActiveAlert): string {
    return alert.campaignTitle?.trim() || 'N/A';
  }

  toggleActiveAlertSort(key: ActiveAlertSortKey): void {
    if (this.activeAlertSortKey() === key) {
      this.activeAlertSortDirection.update((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      this.activeAlertSortKey.set(key);
      this.activeAlertSortDirection.set(key === 'initiatedOn' ? 'desc' : 'asc');
    }
    this.pageIndex.set(0);
  }

  activeAlertSortIcon(key: ActiveAlertSortKey): string | null {
    if (this.activeAlertSortKey() !== key) {
      return null;
    }
    return this.activeAlertSortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  activeAlertColumnLabel(col: (typeof this.activeAlertColumns)[number]): string {
    if (col.labelKey === 'subject') {
      return this.subjectColumnLabel();
    }
    const labels: Record<Exclude<ActiveAlertSortKey, 'alertSubject'>, string> = {
      initiatedOn: 'Created',
      campaignTitle: 'Campaign',
      nextAlertAt: 'Next alert',
      scheduledEndAt: 'End',
      contactPhone: 'Contact',
    };
    return labels[col.key as Exclude<ActiveAlertSortKey, 'alertSubject'>];
  }

  clearSearch(searchInput: HTMLInputElement): void {
    this.searchForm.patchValue({ search: '' });
    searchInput.focus();
  }

  onSelectedAlert(alert: OrgActiveAlert): void {
    const currentPatientId = sessionStorage.getItem('patientId');
    if (currentPatientId !== alert.patientId) {
      sessionStorage.setItem('patientId', alert.patientId);
      this.#notificationViewModel.resetState();
    }

    this.#router.navigate(['/contact'], {
      queryParams: {
        patientId: alert.patientId,
        patientName: alert.alertSubject,
      },
    });
  }

  isAlertStopping(alertId: string): boolean {
    return this.stoppingAlertIds().has(alertId);
  }

  stopActiveAlert(event: Event, alert: OrgActiveAlert): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.isAlertStopping(alert.id)) {
      return;
    }

    this.setAlertStopping(alert.id, true);

    // Cancel API compares Authorization header patientId to body.patientId (see verificatePatientMatches).
    const currentPatientId = sessionStorage.getItem('patientId');
    if (currentPatientId !== alert.patientId) {
      sessionStorage.setItem('patientId', alert.patientId);
    }

    this.#notificationViewModel
      .cancelAlert(alert.patientId, alert.contactPhone, alert.id)
      .pipe(
        take(1),
        finalize(() => this.setAlertStopping(alert.id, false)),
      )
      .subscribe({
        next: (result) => {
          if (result.cancelledCount > 0) {
            this.#snackBar.open(
              'This alert has been stopped. Scheduled calls and texts will stop. ' +
                'A call already handed off to the phone network cannot be cancelled and may still complete.',
              'Okay',
            );
            this.loadActiveAlerts();
          } else {
            this.#snackBar.open('No active alert found for this contact.', 'Okay');
          }
        },
        error: () => {
          this.#snackBar.open('Could not stop the alert. Please try again.', 'Okay', {
            duration: 5000,
          });
        },
      });
  }

  private setAlertStopping(alertId: string, stopping: boolean): void {
    this.stoppingAlertIds.update((ids) => {
      const next = new Set(ids);
      if (stopping) {
        next.add(alertId);
      } else {
        next.delete(alertId);
      }
      return next;
    });
  }
}

function filterActiveAlerts(rows: OrgActiveAlert[], term: string): OrgActiveAlert[] {
  if (!term) {
    return rows;
  }
  return rows.filter((row) => {
    const phoneFormatted = FormUtils.formatPhoneNumber(row.contactPhone).toLowerCase();
    return (
      row.alertSubject.toLowerCase().includes(term) ||
      row.contactPhone.toLowerCase().includes(term) ||
      phoneFormatted.includes(term) ||
      (row.campaignTitle ?? '').toLowerCase().includes(term)
    );
  });
}

function sortActiveAlerts(
  rows: OrgActiveAlert[],
  key: ActiveAlertSortKey,
  direction: 'asc' | 'desc',
): OrgActiveAlert[] {
  const sorted = [...rows];
  const mult = direction === 'asc' ? 1 : -1;
  sorted.sort((a, b) => compareActiveAlerts(a, b, key) * mult);
  return sorted;
}

function compareActiveAlerts(a: OrgActiveAlert, b: OrgActiveAlert, key: ActiveAlertSortKey): number {
  switch (key) {
    case 'initiatedOn':
    case 'nextAlertAt':
    case 'scheduledEndAt':
      return compareOptionalEpoch(a[key], b[key]);
    case 'campaignTitle': {
      const left = (a.campaignTitle?.trim() || 'N/A').toLowerCase();
      const right = (b.campaignTitle?.trim() || 'N/A').toLowerCase();
      return left.localeCompare(right);
    }
    case 'alertSubject':
      return a.alertSubject.localeCompare(b.alertSubject, undefined, { sensitivity: 'base' });
    case 'contactPhone':
      return a.contactPhone.localeCompare(b.contactPhone);
    default:
      return 0;
  }
}

function compareOptionalEpoch(a: number | undefined, b: number | undefined): number {
  const aMissing = a == null || a <= 0;
  const bMissing = b == null || b <= 0;
  if (aMissing && bMissing) {
    return 0;
  }
  if (aMissing) {
    return 1;
  }
  if (bMissing) {
    return -1;
  }
  return a - b;
}
